import { Injectable, LOCALE_ID, inject } from '@angular/core';
import { InombraRoutes } from 'src/app/models/routes';
import { AuthService } from 'src/app/shared/services/auth.service';
import { Router } from '@angular/router';
import moment from 'moment';
import { Address } from 'src/app/models';
import { UserHttpService } from './http/user-http.service';

enum TokenStatus {
  valid = 'valid',
  expired = 'expired',
  inexistent = 'inexistent',
  obsolete = 'obsolete',
}

@Injectable({
  providedIn: 'root',
})
export class Utils {
  readonly authService = inject(AuthService);
  readonly router = inject(Router);
  readonly locale = inject(LOCALE_ID);
  readonly userHttp = inject(UserHttpService);

  public static readonly AuthExceptions = [
    InombraRoutes.ResetPassword,
    InombraRoutes.Login,
    InombraRoutes.Invoice,
    InombraRoutes.File,
    InombraRoutes.Gallery,
  ];

  static get isAuthException() {
    const path = window.location.pathname;
    return Utils.AuthExceptions.some(i => path.includes(i));
  }

  get token(): string | null {
    return localStorage.getItem('token');
  }

  get tokenDecoded(): Array<any> {
    return !this.token
      ? []
      : this.token
          .split('.')
          .splice(0, 2)
          .map(i => JSON.parse(window.atob(i)));
  }

  tokenTTL(): number {
    if (!this.token) {
      return 0;
    }

    // time in seconds until expiration
    const now = new Date().getTime() / 1000;
    const then = this.tokenDecoded[1].exp;
    const diff = then - now;
    return Math.round(diff);
  }

  tokenStatus(): TokenStatus {
    if (!this.token) {
      return TokenStatus.inexistent;
    }

    const ttl = this.tokenTTL();
    if (ttl <= 2) {
      return TokenStatus.expired;
    } else if (ttl <= 3600) {
      return TokenStatus.obsolete;
    } else {
      return TokenStatus.valid;
    }
  }

  tokenCheckRoutine(): void {
    const status = this.tokenStatus();
    if (!Utils.isAuthException) {
      if (status === TokenStatus.obsolete) {
        this.refreshToken();
      } else if (
        [TokenStatus.inexistent, TokenStatus.expired].includes(status)
      ) {
        this.authService.logout();
        this.router.navigate([InombraRoutes.Login]);
      }
    }
  }

  refreshToken(): void {
    if (!Utils.isAuthException) {
      this.userHttp.getRefreshToken().subscribe(res => {
        this.authService.setToken(res['token']);
      });
    }
  }

  dateDiff(date, unit = 'days') {
    try {
      const currentDate = moment();
      const inputDate = moment(date);
      return Math.floor(currentDate.diff(inputDate, unit as any));
    } catch (err) {
      return 0;
    }
  }

  isWinterTime(futureDate) {
    // Get the current date and the future date as Date objects
    const currentDate = new Date();
    const targetDate = new Date(futureDate);

    // Get the time zone offsets in minutes (negative value for west of UTC)
    const currentOffset = currentDate.getTimezoneOffset();
    const targetOffset = targetDate.getTimezoneOffset();

    // Difference between current and future time offsets
    const offsetDifference = targetOffset - currentOffset;

    // Return true if the difference is 60 minutes (1 hour), indicating winter time
    return offsetDifference === 60;
  }

  objectKeyNameFormatting(obj) {
    /*
      const data = {
        first_name: "John"
      }

      const data = {
        "First Name": "John"
      }
    */

    const result = {};
    Object.entries(obj).forEach(([key, value]) => {
      const readableKey = key
        .replace(/([a-z])([A-Z])/g, '$1 $2') // camelCase to space-separated
        .replace(/_/g, ' ') // snake_case to space-separated
        .replace(/\b\w/g, char => char.toUpperCase()); // Capitalize each word
      result[readableKey] = value;
    });
    return result;
  }

  formatAddress(
    address: Partial<Address>,
    mapping = {
      postcode: 'postcode',
      address1: 'address1',
      address2: 'address2',
      city: 'city',
    }
  ): string {
    if (!address) return '';

    const getValue = key => address[mapping[key]];

    const parts = [
      getValue('postcode'),
      getValue('address1'),
      getValue('address2'),
      getValue('city'),
    ];

    const formattedParts = parts.filter(
      part => part != null && part.toString().trim() !== ''
    );

    return formattedParts.join(', ');
  }

  // Legacy
  static onCacheBlockLoaded(gridParams, cb) {
    let interval = setInterval(() => {
      if (gridParams) {
        if (checkIfBlocksLoaded(gridParams.api)) {
          clearInterval(interval);
          cb(gridParams);
        }
      }
    }, 50);

    function checkIfBlocksLoaded(api) {
      if (api.getCacheBlockState() === null) return false;
      if (api.getCacheBlockState() === undefined) return false;

      const status = api.getCacheBlockState()[0]
        ? api.getCacheBlockState()[0].pageStatus
        : false;

      return status === 'loaded';
    }
  }
}
